import { type SetStateAction, type ChangeEvent, type Dispatch, useMemo } from 'react';
import { MinusIconV2, PlusIcon } from 'ui-kit/assets';
import { type Fields } from 'types/builder/interface';
import { type ReactFCC } from 'types/react';

import { ButtonsContainer, Container, Content, Button, Title } from '../styles';
import { type FormFieldsErrors, type VariationItems } from '../../types';
import { InputWithTooltip } from './InputWithTooltip';
import { Logic } from '../../tabs/Logic';

const portal = document.createElement('div');
if (!document.body) {
    throw new Error('body not ready for portal creation!');
}
document.body.appendChild(portal);

export interface AddOrRemoveButtonProps {
    type?: 'remove' | 'add';
    itemType?: string;
    index: number;
}

interface Props {
    handleAddOrRemoveRadioOrMultiSelect: (props: AddOrRemoveButtonProps) => void;
    setVariationItems: Dispatch<SetStateAction<VariationItems[]>>;
    formValues?: { [key: string]: string };
    variationItems: VariationItems[];
    isDisabledEditInput: boolean;
    errors: FormFieldsErrors[];
    isShownLogicTab: boolean;
    selectedValue: string;
    blockId?: string;
    clause?: Fields;
}

export const VariationsEditor: ReactFCC<Props> = ({
    handleAddOrRemoveRadioOrMultiSelect,
    isDisabledEditInput,
    setVariationItems,
    isShownLogicTab,
    variationItems,
    selectedValue,
    formValues,
    blockId,
    errors,
    clause
}) => {
    const filteredErrors = useMemo(() => errors.filter(item => item.name !== 'value'), [errors]);
    const handleChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
        const { value } = event.target;

        const newState = variationItems.map((item, i) => (i === index ? { ...item, text: value } : item));

        setVariationItems(newState);
    };

    return (
        <>
            <Container>
                <Title>Answers</Title>
                {!!variationItems.length &&
                    variationItems.map(({ text, id }, index) => {
                        const error = filteredErrors.find(item => item.name.includes(String(index + 1)))?.error;
                        return (
                            <Content key={id}>
                                <InputWithTooltip
                                    isDisabledEditInput={isDisabledEditInput}
                                    name={`radio_${index + 1}_text`}
                                    handleChange={handleChange}
                                    error={error}
                                    index={index}
                                    text={text}
                                />
                                {!isDisabledEditInput && (
                                    <ButtonsContainer>
                                        <Button
                                            onClick={() => {
                                                handleAddOrRemoveRadioOrMultiSelect({
                                                    type: 'add',
                                                    index
                                                });
                                            }}
                                            type="button"
                                        >
                                            <PlusIcon height={12} width={12} />
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                handleAddOrRemoveRadioOrMultiSelect({
                                                    type: 'remove',
                                                    index
                                                });
                                            }}
                                            type="button"
                                        >
                                            <MinusIconV2 height={2} width={12} />
                                        </Button>
                                    </ButtonsContainer>
                                )}
                            </Content>
                        );
                    })}
            </Container>
            {isShownLogicTab && (
                <Logic
                    variationItems={variationItems}
                    selectedValue={selectedValue}
                    formValues={formValues}
                    blockId={blockId}
                    clause={clause}
                />
            )}
        </>
    );
};
